import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { AppDataService } from 'src/app/core/app-data/app-data.service';
import { FEATURE_FLAGS } from '../../api.constants';
import { FeatureFlagKey } from './feature-flag.constant';
import { FeatureFlagDefinition, FeaturesApiResponse } from './feature-flag.types';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {

    /** How long a successful load is trusted before `ensureLoaded()` will revalidate it. */
    private static readonly STALE_AFTER_MS = 5 * 60 * 1000;

    private readonly _flags = new BehaviorSubject<Map<string, FeatureFlagDefinition>>(new Map());
    private readonly _cancel$ = new Subject<void>();
    private _load$: Observable<void> | null = null;
    private _inFlight = false;
    private _loadedAt: number | null = null;

    constructor(private appDataService: AppDataService) {}

    load(): Observable<void> {
        if (!this._load$) {
            this._inFlight = true;
            this._load$ = this.appDataService.get<FeaturesApiResponse>(FEATURE_FLAGS, {}).pipe(
                takeUntil(this._cancel$),
                map(response => this.normalize(response)),
                tap(flags => {
                    this._flags.next(flags);
                    this._loadedAt = Date.now();
                }),
                map(() => undefined),
                catchError(() => {
                    // Allow a future call to retry. Keep any previously-loaded flags
                    // intact so a failed revalidation doesn't hide a control that was
                    // already correctly showing based on an earlier successful load.
                    this._load$ = null;
                    if (this._flags.getValue().size === 0) {
                        this._flags.next(new Map());
                    }
                    return of(undefined);
                }),
                finalize(() => { this._inFlight = false; }),
                shareReplay(1),
            );
        }
        return this._load$!;
    }

    /**
     * Ensures flags are loaded and reasonably fresh, without firing a duplicate
     * request on top of one already in flight (e.g. from a route resolver) and
     * without treating "loaded, flag legitimately absent" the same as "load failed".
     * Refetches only when there's no completed/in-flight load, the last attempt
     * failed, or the last successful load is older than `STALE_AFTER_MS`.
     */
    ensureLoaded(): Observable<void> {
        if (this._load$ && (this._inFlight || !this.isStale())) {
            return this._load$;
        }
        this._load$ = null;
        return this.load();
    }

    reset(): void {
        this._cancel$.next();
        this._load$ = null;
        this._inFlight = false;
        this._loadedAt = null;
        this._flags.next(new Map());
    }

    private isStale(): boolean {
        return this._loadedAt === null || (Date.now() - this._loadedAt) > FeatureFlagService.STALE_AFTER_MS;
    }

    isEnabled(name: string | FeatureFlagKey): boolean {
        return this._flags.getValue().get(name)?.enabled ?? false;
    }

    isEnabled$(name: string | FeatureFlagKey): Observable<boolean> {
        return this._flags.pipe(map(flags => flags.get(name)?.enabled ?? false));
    }

    getAttributes<TAttr>(name: string | FeatureFlagKey): TAttr | undefined {
        const flag = this._flags.getValue().get(name);
        return flag ? (flag.attributes as unknown as TAttr) : undefined;
    }

    getAttributes$<TAttr>(name: string | FeatureFlagKey): Observable<TAttr | undefined> {
        return this._flags.pipe(
            map(flags => {
                const flag = flags.get(name);
                return flag ? (flag.attributes as unknown as TAttr) : undefined;
            })
        );
    }

    private normalize(response: FeaturesApiResponse): Map<string, FeatureFlagDefinition> {
        const map = new Map<string, FeatureFlagDefinition>();
        Object.entries(response.features ?? {}).forEach(([name, dto]) => {
            const entry: FeatureFlagDefinition = {
                name,
                enabled: dto.enabled,
                attributes: dto.attributes ?? {},
            };
            map.set(name, entry);
        });
        return map;
    }
}
